import { useLayoutEffect, type RefObject } from "react";
import { useCompactAgentDisplayStore } from "@/stores/compact-agent-display-store";
import { rectCenterAnchor } from "./onboarding/cursor-target-anchor";
import { getWindowDisplayId } from "./use-window-role";

function useCompactAgentAnchor({
  anchorRef,
  agentId,
  enabled,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  agentId: string | null;
  enabled: boolean;
}) {
  useLayoutEffect(() => {
    const element = anchorRef.current;
    if (!enabled || !agentId || !element) {
      useCompactAgentDisplayStore.getState().setFirstAvatarAnchor(null);
      return;
    }
    const publishAnchor = () => {
      const rect = element.getBoundingClientRect();
      const anchor = rectCenterAnchor(rect, { dockAvatar: true });
      useCompactAgentDisplayStore.getState().setFirstAvatarAnchor({
        agentId,
        displayId: getWindowDisplayId(),
        x: Math.max(12, Math.min(window.innerWidth - 28, anchor.x)),
        y: Math.max(12, anchor.y),
        updatedAt: Date.now(),
      });
    };
    publishAnchor();
    const frame = window.requestAnimationFrame(publishAnchor);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(publishAnchor);
    observer?.observe(element);
    window.addEventListener("resize", publishAnchor);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", publishAnchor);
      const current = useCompactAgentDisplayStore.getState().firstAvatarAnchor;
      if (current?.agentId === agentId) {
        useCompactAgentDisplayStore.getState().setFirstAvatarAnchor(null);
      }
    };
  }, [agentId, anchorRef, enabled]);
}

export { useCompactAgentAnchor };
